import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../Services/loginService";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import api, { API_BASE_URL } from "../../src/utils/api";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🔹 Handle User Login
  const handleLogin = async () => {
    try {
      const user = await loginUser(username, password);

      // Save essentials
      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("roleId", user.roleId);

      // Save username only if Remember Me checked
      if (rememberMe) {
        localStorage.setItem("savedUsername", username);
      } else {
        localStorage.removeItem("savedUsername");
      }

      // Navigate
      setTimeout(() => {
        if (user.roleId === 3) {
          navigate("/home/memberspage", { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      }, 0);
    } catch (error) {
      setErrorMessage("Invalid username or password");
      setOpenSnackbar(true);
    }
  };

  // 🔹 Form Submit Handler
  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  // 🔹 Handle Forgot Password
  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setErrorMessage("Please enter your email.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset email");
      }

      setEmailSent(true);
      setTimeout(() => {
        setShowForgotPasswordModal(false);
        setEmailSent(false);
      }, 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setOpenSnackbar(true);
    }
  };

  const handleRequestAccess = async () => {
    if (!requestName || !requestEmail || !requestReason) {
      setErrorMessage("All fields are required.");
      setOpenSnackbar(true);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(requestEmail)) {
      setErrorMessage("Please enter a valid email.");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`${API_BASE_URL}/request-access`, {
        name: requestName,
        email: requestEmail,
        reason: requestReason,
      });

      if (response.status === 200) {
        setRequestSuccess(true);
        setTimeout(() => {
          setShowRequestAccessModal(false);
          setRequestSuccess(false);
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to submit request.");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
      setOpenSnackbar(true);
    }
  };

  // 🔹 Auto-fill username if saved
  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "400px",
          padding: "1.5rem 2rem 2rem 2rem",
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* 🔹 Logo */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1.25rem",
            marginTop: "-0.5rem",
          }}
        >
          <img
            src="/img/logo.png"
            alt="Logo"
            style={{
              width: "240px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* 🔹 Login Form */}
        <form method="post" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            autoComplete="username"
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ marginBottom: "1rem" }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ marginBottom: "1rem" }}
          />

          {/* 🔹 Remember Me */}
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
              />
            }
            label="Remember Me"
            sx={{ marginBottom: "1rem", textAlign: "left" }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#007bff",
              color: "white",
              fontWeight: "bold",
              padding: "0.75rem",
              "&:hover": { backgroundColor: "#0056b3" },
            }}
          >
            Log In
          </Button>
        </form>

        <Box
          sx={{
            marginTop: "1.5rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Link
            href="#"
            underline="hover"
            sx={{ fontSize: "0.875rem" }}
            onClick={() => setShowForgotPasswordModal(true)}
          >
            Forgot your password?
          </Link>
          
        </Box>

        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            bottom: "16px",
            left: 0,
            right: 0,
            textAlign: "center",
            fontWeight: 500,
            color: "#666",
          }}
        >
          © My HOA {new Date().getFullYear()}
        </Typography>
      </Box>

      {/* 🔹 Forgot Password Modal */}
      <Dialog
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {emailSent ? (
            <Typography color="success" sx={{ fontWeight: "bold" }}>
              ✅ Reset link sent! Check your email.
            </Typography>
          ) : (
            <>
              <Typography>
                Please enter your email to receive a reset link:
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                sx={{ marginTop: "1rem" }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!emailSent && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              color="primary"
            >
              Send Reset Link
            </Button>
          )}
          <Button
            onClick={() => setShowForgotPasswordModal(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔹 Request Access Modal */}
      <Dialog
        open={showRequestAccessModal}
        onClose={() => setShowRequestAccessModal(false)}
      >
        <DialogTitle>Request Access</DialogTitle>
        <DialogContent>
          {requestSuccess ? (
            <Typography color="success">
              ✅ Request submitted! We will contact you soon.
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                sx={{ marginTop: "1rem" }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                sx={{ marginTop: "1rem" }}
              />
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                variant="outlined"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                sx={{ marginTop: "1rem" }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!requestSuccess && (
            <Button
              onClick={handleRequestAccess}
              variant="contained"
              color="primary"
            >
              Submit Request
            </Button>
          )}
          <Button
            onClick={() => setShowRequestAccessModal(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔹 Snackbar for Errors */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
